import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { MIN_PASSWORD_LENGTH } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { LANGUAGES, SALUTATIONS } from 'projects/core/src/lib/constants';
import { RoleModel } from 'projects/core/src/lib/models/role.model';
import { TeamModel } from 'projects/core/src/lib/models/team.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { forkJoin } from 'rxjs';

const SEPARATOR = ',';

@Component({
  selector: 'app-user-basedata',
  templateUrl: './user-basedata.component.html',
  styleUrls: ['./user-basedata.component.scss']
})
export class UserBasedataComponent extends BaseForm implements OnInit, OnChanges {

  @Input() user: UserModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;
  public roles: Array<RoleModel> = [];
  public country: string | null = null;
  public timezone: string | null = null;
  public salutations: Array<SelectItem> = [];
  public languages: Array<SelectItem> = [];
  public teams: Array<TeamModel> = [];

  public form = this.fb.group({
    gender: new UntypedFormControl('', [Validators.required]),
    firstname: new UntypedFormControl('', [Validators.required]),
    lastname: new UntypedFormControl('', [Validators.required]),
    roles: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.email()]),
    phone: new UntypedFormControl(''),
    mobile: new UntypedFormControl(''),
    team: new UntypedFormControl(''),
    company: new UntypedFormControl(''),
    language: new UntypedFormControl('', [Validators.required]),
    comment: new UntypedFormControl(''),
    alt_emails: new UntypedFormControl(''),
    passwords: this.fb.group({
      password: new UntypedFormControl(''),
      confirm: new UntypedFormControl(''),
    }, {
      validators: [this.passwordValidator]
    }),
    tz: this.fb.group({}),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.salutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.teams = [];
    this.roles = [];
    forkJoin([
      this.api.getModels('roles'),
      this.api.getModels('teams'),
    ])
    .subscribe({
      next: (result: any) => {
        if (result[0] !== null && result[0].data.length > 0) {
          result[0].data.forEach((role: any) => {
            role = new RoleModel().deserialize(role);
            role.setTranslocoService(this.transloco);
            this.roles.push(role);
          });
        }
        if (result[1] !== null && result[1].data.length > 0) {
          result[1].data.forEach((team: any) => {
            this.teams.push(new TeamModel().deserialize(team));
          });
        }
        this.loading = false;
        this.ui.setFocus();
      }
    });
    this.country = this.user.country;
    this.timezone = this.user.timezone;
  }

  ngOnChanges(): void {
    this.items = [];
    if (this.user !== null) {
      if (this.auth.hasRight('admin.user.delete')) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.user.deletable),
          command: () => {
            this.onDelete();
          }
        });
      }
    }
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        id: this.user.id,
        gender: this.form.value.gender,
        firstname: this.form.value.firstname,
        lastname: this.form.value.lastname,
        email: this.form.value.email,
        phone: this.form.value.phone,
        mobile: this.form.value.mobile,
        team: this.form.value.team?.id,
        language: this.form.value.language,
        company: this.form.value.company,
        comment: this.form.value.comment,
        password: this.form.value.passwords.password,
        country: this.country,
        timezone: this.timezone,
        misc: this.form.value.misc,
        alt_emails: this.getAlternativeEmailPayload()
      };
      if (this.form.value.passwords.password) {
        payload.password = this.form.value.passwords.password;
      }
      const roles = [];
      this.form.value?.roles?.forEach((role: RoleModel) => {
        roles.push(role.id);
      });
      payload.roles = roles;
      this.api.saveModel('users', this.user.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.firstname + ' ' + this.form.value.lastname }), this.transloco.translate('common.saved'));
          this.user.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  private getAlternativeEmailPayload(): Array<string> | null {
    let input = this.form.value.alt_emails;
    input = input.replace(/[,;|\+:\/\n]/g, SEPARATOR);
    const result = input.split(SEPARATOR);
    return (Array.isArray(result) && result.length > 0) ? result : null;
  }

  onDelete(): void {
    if (this.auth.hasRight('admin.user.delete')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.user.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('users', this.user.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.user.fullname }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/users']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  protected startEdit(): void {
    if (this.user !== null) {
      this.form.patchValue({
        gender: this.user.gender,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        email: this.user.email,
        phone: this.user.phone,
        mobile: this.user.mobile,
        team: this.user.team,
        language: this.user.language,
        company: this.user.company,
        comment: this.user.comment,
        misc: this.user.misc,
        roles: this.user.roles,
        alt_emails: this.user.alt_emails?.join(SEPARATOR) || '',
      });
    }
    this.setEdit.emit('basedata');
    super.startEdit();
    this.ui.setFormFocus();
  }

  protected stopEdit(): void {
    this.form.reset();
    this.country = this.user.country;
    this.timezone = this.user.timezone;
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
  }

  private passwordValidator(group: UntypedFormGroup): void {
    const password = group.get('password')?.value;
    const password2 = group.get('confirm')?.value;
    group.get('password').setErrors(null);
    group.get('confirm').setErrors(null);
    if (password || password2) {
      if (!password) {
        group.get('password')?.setErrors({ required: true });
      } else {
        const errors = [];
        if (password.length < MIN_PASSWORD_LENGTH) {
          errors.push('passwordLength');
        }
        if (!(/\d/.test(password) && /[A-Z]/.test(password) && /[a-z]/.test(password))) {
          errors.push('passwordStrength');
        }
        if (errors.length > 0) {
          const errorList = {};
          errors.forEach((error: string) => {
            errorList[error] = true;
          });
          group.get('password')?.setErrors(errorList);
        } else {
          group.get('password')?.setErrors(null);
        }
        if (!password2) {
          group.get('confirm')?.setErrors({ required: true });
        } else if (password !== password2) {
          group.get('confirm')?.setErrors({ passwordMatch: true });
        }
      }
    }
  }

  public getUserRoleNames(): Array<string> {
    return this.user.roles.map((role: RoleModel) => {
      role.setTranslocoService(this.transloco);
      return role.translated_title;
    });
  }
}
